/**
 * RichTextToolbar - Floating formatting toolbar for rich text fields
 *
 * Key features:
 * - Only activates for elements with data-field-type="rich_text"
 * - Floating toolbar appears above text selection
 * - Bold (Cmd+B), Italic (Cmd+I), Link (Cmd+K) buttons
 * - Clear formatting button
 * - Selection-aware button states
 *
 * Implementation: Phase 2 Task 3.3
 *
 * Usage:
 *   const richTextToolbar = new RichTextToolbar()
 *   richTextToolbar.initialize() // Sets up selection listeners
 *   richTextToolbar.destroy()    // Cleanup
 */

export class RichTextToolbar {
  constructor(options = {}) {
    this.options = {
      containerSelector: '[data-field-type="rich_text"]',
      offsetY: 10, // Distance above selection
      ...options
    }

    this.toolbar = null
    this.currentElement = null
    this.isVisible = false

    // Bound handlers for cleanup
    this.handleSelectionChange = this.handleSelectionChange.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleMouseDown = this.handleMouseDown.bind(this)
  }

  /**
   * Initialize the toolbar and set up event listeners
   */
  initialize() {
    this.createToolbar()
    this.setupEventListeners()
    console.log('[RichTextToolbar] Initialized')
  }

  /**
   * Create the toolbar DOM element
   * @private
   */
  createToolbar() {
    this.toolbar = document.createElement('div')
    this.toolbar.className = 'rich-text-toolbar'
    this.toolbar.setAttribute('role', 'toolbar')
    this.toolbar.setAttribute('aria-label', 'Text formatting')

    // Button definitions: [id, icon, label, command]
    const buttons = [
      ['bold', 'B', 'Bold (Cmd+B)', 'bold'],
      ['italic', 'I', 'Italic (Cmd+I)', 'italic'],
      ['divider', null, null, null],
      ['link', '🔗', 'Link (Cmd+K)', 'link'],
      ['divider', null, null, null],
      ['clear', '✕', 'Clear formatting', 'clear']
    ]

    buttons.forEach(([id, icon, label, command]) => {
      if (id === 'divider') {
        const divider = document.createElement('div')
        divider.className = 'rich-text-toolbar__divider'
        this.toolbar.appendChild(divider)
        return
      }

      const button = document.createElement('button')
      button.type = 'button'
      button.className = `rich-text-toolbar__btn rich-text-toolbar__btn--${id}`
      button.dataset.command = command
      button.setAttribute('aria-label', label)
      button.setAttribute('title', label)

      // Use styled text for bold/italic to show effect
      if (id === 'bold') {
        button.innerHTML = '<strong>B</strong>'
      } else if (id === 'italic') {
        button.innerHTML = '<em>I</em>'
      } else {
        button.textContent = icon
      }

      button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.executeCommand(command)
      })

      this.toolbar.appendChild(button)
    })

    // Add to document but keep hidden
    this.toolbar.style.display = 'none'
    document.body.appendChild(this.toolbar)
  }

  /**
   * Set up document-level event listeners
   * @private
   */
  setupEventListeners() {
    // Listen for selection changes
    document.addEventListener('selectionchange', this.handleSelectionChange)

    // Listen for keyboard shortcuts
    document.addEventListener('keydown', this.handleKeydown, true)

    // Hide on mousedown outside toolbar
    document.addEventListener('mousedown', this.handleMouseDown)
  }

  /**
   * Handle selection changes to show/hide toolbar
   * @private
   */
  handleSelectionChange() {
    const selection = window.getSelection()

    // Hide if no selection or collapsed
    if (!selection || selection.isCollapsed) {
      this.hide()
      return
    }

    // Get the anchor node's parent element
    const anchorNode = selection.anchorNode
    if (!anchorNode) {
      this.hide()
      return
    }

    const element = anchorNode.nodeType === Node.TEXT_NODE ?
      anchorNode.parentElement :
      anchorNode

    // Find the rich text container
    const richTextContainer = element?.closest(this.options.containerSelector)

    if (!richTextContainer) {
      this.hide()
      return
    }

    // Show toolbar for this selection
    this.currentElement = richTextContainer
    this.show(selection)
    this.updateButtonStates()
  }

  /**
   * Handle keyboard shortcuts
   * @private
   */
  handleKeydown(e) {
    // Only handle if we're in a rich text field
    const activeElement = document.activeElement
    const richTextContainer = activeElement?.closest(this.options.containerSelector)

    if (!richTextContainer) return

    // Check for Cmd/Ctrl key combinations
    const isCmdOrCtrl = e.metaKey || e.ctrlKey

    if (isCmdOrCtrl && !e.shiftKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault()
          this.executeCommand('bold')
          break
        case 'i':
          e.preventDefault()
          this.executeCommand('italic')
          break
        case 'k':
          e.preventDefault()
          this.executeCommand('link')
          break
      }
    }
  }

  /**
   * Handle mousedown to hide toolbar when clicking outside
   * @private
   */
  handleMouseDown(e) {
    // Don't hide if clicking the toolbar itself
    if (this.toolbar && this.toolbar.contains(e.target)) {
      return
    }

    // Don't hide if clicking in a rich text field
    const richTextContainer = e.target.closest(this.options.containerSelector)
    if (richTextContainer) {
      return
    }

    this.hide()
  }

  /**
   * Show the toolbar positioned above the selection
   * @private
   */
  show(selection) {
    if (!selection || selection.isCollapsed) return

    const range = selection.getRangeAt(0)
    const rect = range.getBoundingClientRect()

    // Position above the selection
    const toolbarRect = this.toolbar.getBoundingClientRect()
    const left = rect.left + (rect.width / 2) - (toolbarRect.width / 2)
    const top = rect.top - toolbarRect.height - this.options.offsetY + window.scrollY

    // Keep within viewport
    const adjustedLeft = Math.max(10, Math.min(left, window.innerWidth - toolbarRect.width - 10))
    const adjustedTop = Math.max(10, top)

    this.toolbar.style.left = `${adjustedLeft}px`
    this.toolbar.style.top = `${adjustedTop}px`
    this.toolbar.style.display = 'flex'
    this.isVisible = true
  }

  /**
   * Hide the toolbar
   * @private
   */
  hide() {
    if (!this.isVisible) return

    this.toolbar.style.display = 'none'
    this.isVisible = false
    this.currentElement = null
  }

  /**
   * Update button active states based on current selection
   * @private
   */
  updateButtonStates() {
    const boldBtn = this.toolbar.querySelector('[data-command="bold"]')
    const italicBtn = this.toolbar.querySelector('[data-command="italic"]')

    if (boldBtn) {
      const isBold = document.queryCommandState('bold')
      boldBtn.classList.toggle('active', isBold)
    }

    if (italicBtn) {
      const isItalic = document.queryCommandState('italic')
      italicBtn.classList.toggle('active', isItalic)
    }
  }

  /**
   * Execute a formatting command
   * @param {string} command - The command to execute
   */
  executeCommand(command) {
    // Focus the element to ensure execCommand works
    if (this.currentElement) {
      this.currentElement.focus()
    }

    switch (command) {
      case 'bold':
        document.execCommand('bold', false, null)
        break

      case 'italic':
        document.execCommand('italic', false, null)
        break

      case 'link':
        this.insertLink()
        break

      case 'clear':
        document.execCommand('removeFormat', false, null)
        break
    }

    // Update button states after command
    this.updateButtonStates()

    // Trigger input event for save queue
    if (this.currentElement) {
      this.currentElement.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  /**
   * Insert or edit a link
   * @private
   */
  insertLink() {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) {
      return
    }

    // Check if selection is already a link
    const parentLink = selection.anchorNode?.parentElement?.closest('a')
    const currentHref = parentLink?.href || ''

    // Simple prompt for now (can be enhanced with modal later)
    const url = prompt('Enter URL:', currentHref || 'https://')

    if (url === null) {
      // User cancelled
      return
    }

    if (url === '') {
      // Remove link if URL is empty
      if (parentLink) {
        document.execCommand('unlink', false, null)
      }
      return
    }

    // Validate URL has safe protocol
    const safeProtocols = ['http://', 'https://', 'mailto:', 'tel:', '/']
    const isSafe = safeProtocols.some((p) => url.toLowerCase().startsWith(p))

    if (!isSafe) {
      alert('Invalid URL. Must start with http://, https://, mailto:, tel:, or /')
      return
    }

    document.execCommand('createLink', false, url)

    // Add target="_blank" and rel="noopener noreferrer" for external links
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const links = this.currentElement.querySelectorAll(`a[href="${url}"]`)
      links.forEach((link) => {
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
      })
    }
  }

  /**
   * Clean up and remove the toolbar
   */
  destroy() {
    document.removeEventListener('selectionchange', this.handleSelectionChange)
    document.removeEventListener('keydown', this.handleKeydown, true)
    document.removeEventListener('mousedown', this.handleMouseDown)

    if (this.toolbar && this.toolbar.parentNode) {
      this.toolbar.parentNode.removeChild(this.toolbar)
    }

    this.toolbar = null
    this.currentElement = null
    this.isVisible = false

    console.log('[RichTextToolbar] Destroyed')
  }
}
